import { useIsVisglMapsEnabled } from 'featureFlags/useIsVisglMapsEnabled';
import { useLoadGoogleMaps } from 'components/maps/useLoadGoogleMaps';
import MapsApiProvider from 'maps/MapsApiProvider';

interface LoadGoogleMapsProps {
  children?: React.ReactNode;
}

/**
 * Legacy (flag-OFF) loader: loads the Maps JS API via `@react-google-maps/api`
 * `useLoadScript` and only renders children once the API is ready.
 */
// TODO(visgl-burn-in): remove `LegacyLoadGoogleMaps` and the flag fork in
// `LoadGoogleMaps` below (keep the `VisglLoadGoogleMaps` path) once
// CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully rolled out.
const LegacyLoadGoogleMaps = ({ children }: LoadGoogleMapsProps) => {
  const { isLoaded, loadError } = useLoadGoogleMaps();
  if (children && isLoaded && !loadError) {
    return <>{children}</>;
  }
  return null;
};

/**
 * New (flag-ON) loader: mounts a `MapsApiProvider` (single `<APIProvider>` +
 * load-state bridge) which loads the Maps JS API once process-wide, dispatches
 * the existing Redux load-state, and provides vis.gl context to any children.
 */
const VisglLoadGoogleMaps = ({ children }: LoadGoogleMapsProps) => (
  <MapsApiProvider>{children}</MapsApiProvider>
);

/**
 * Stable seam used by every maps-backed route/form. Call sites never change:
 * flag-OFF keeps today's `useLoadScript` behavior (render children only when
 * loaded); flag-ON mounts the vis.gl provider. Map components rendered as
 * siblings (not children) self-wrap their own `MapsApiProvider`, so vis.gl
 * context is guaranteed regardless of whether this seam wraps them.
 */
export const LoadGoogleMaps = ({ children }: LoadGoogleMapsProps) => {
  const visglEnabled = useIsVisglMapsEnabled();
  return visglEnabled ? (
    <VisglLoadGoogleMaps>{children}</VisglLoadGoogleMaps>
  ) : (
    <LegacyLoadGoogleMaps>{children}</LegacyLoadGoogleMaps>
  );
};
